import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { ChevronLeft, Play, Pause } from 'lucide-react'
import { productById } from './registry'
import { useProductFeed } from '@/lib/feeds/client'
import { ConfigHint } from '@/components/ConfigHint'
import { SkeletonBlock, ErrorState } from '@/components/feedback'
import { hasR2 } from '@/config'
import { fmtTime } from '@/lib/time'
import './products.css'

export function ProductDetailView() {
  const { productId } = useParams()
  const product = productById(productId)
  const feed = useProductFeed(product?.feedUrl)

  const frames = feed.data?.frames ?? []
  const [idx, setIdx] = useState(0)
  const [playing, setPlaying] = useState(false)

  // Default to the latest frame once loaded.
  useEffect(() => {
    if (frames.length) setIdx(frames.length - 1)
  }, [frames.length])

  // Simple animation loop over frames.
  useEffect(() => {
    if (!playing || frames.length < 2) return
    const t = setInterval(() => setIdx((i) => (i + 1) % frames.length), 450)
    return () => clearInterval(t)
  }, [playing, frames.length])

  if (!product) {
    return <ErrorState message="Unknown product." />
  }

  // Clamp during render: a poll can return fewer frames than before, leaving `idx`
  // transiently out of range until the seeding effect runs post-commit.
  const safeIdx = Math.min(idx, Math.max(frames.length - 1, 0))
  const frame = frames[safeIdx]

  return (
    <div className="pdetail" style={{ '--accent': product.accent } as React.CSSProperties}>
      <header className="pdetail__head">
        <Link to="/products" className="pdetail__back" aria-label="Back to products">
          <ChevronLeft size={20} />
        </Link>
        <div>
          <h1 className="pdetail__title">{product.title}</h1>
          <p className="pdetail__sub">{product.subtitle}</p>
        </div>
      </header>

      {!hasR2 ? (
        <ConfigHint envVar={product.envVar} what={product.title.toLowerCase()} />
      ) : feed.isLoading ? (
        <SkeletonBlock height={320} radius={18} />
      ) : feed.isError ? (
        <ErrorState message="Couldn’t load this feed." onRetry={() => feed.refetch()} />
      ) : frames.length === 0 ? (
        <ConfigHint envVar={product.envVar} what={product.title.toLowerCase()} />
      ) : (
        <>
          <div className="pdetail__stage">
            <img src={frame.url} alt={`${product.title} ${frame.label ?? ''}`} className="pdetail__img" />
            <div className="pdetail__stamp">
              {frame.label ?? (frame.timestamp ? fmtTime(frame.timestamp) : `Frame ${safeIdx + 1}`)}
            </div>
          </div>

          {frames.length > 1 && (
            <div className="pdetail__controls">
              <button
                className="pdetail__play"
                onClick={() => setPlaying((p) => !p)}
                aria-label={playing ? 'Pause' : 'Play'}
              >
                {playing ? <Pause size={18} /> : <Play size={18} />}
              </button>
              <input
                type="range"
                min={0}
                max={frames.length - 1}
                value={safeIdx}
                onChange={(e) => {
                  setPlaying(false)
                  setIdx(Number(e.target.value))
                }}
                className="pdetail__scrub"
              />
              <span className="pdetail__count num">
                {safeIdx + 1}/{frames.length}
              </span>
            </div>
          )}
        </>
      )}
    </div>
  )
}
